import React, { useMemo, useState } from "react";
import SchemeModal from "../../schemeModal/SchemeModal";
import { parseCoordinate } from "../../../utils/coords";
import {
  formatDate,
  formatMeters,
  formatNumber,
  formatPercent,
  formatVerificationStatus,
  fullName,
  hasDisplayValue,
  negativeReasonEntries,
  statusClass,
  statusLabel,
} from "./inspectionStatus";

const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";

function Progress({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="inspection-progress">
      <div className="inspection-progress__bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatGrid({ items }) {
  const visibleItems = (items || []).filter((item) => hasDisplayValue(item.value));
  if (!visibleItems.length) return null;

  return (
    <div className="inspection-stat-grid">
      {visibleItems.map((item) => (
        <div key={item.label} className="inspection-stat">
          <span className="inspection-stat__label">{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

function InlineMeta({ items }) {
  const visibleItems = (items || []).filter((item) => hasDisplayValue(item));
  if (!visibleItems.length) return null;

  return (
    <div className="inspection-panel__meta">
      {visibleItems.map((item) => <span key={item}>{item}</span>)}
    </div>
  );
}

function FieldGrid({ items }) {
  const visibleItems = (items || []).filter((item) => hasDisplayValue(item.value));
  if (!visibleItems.length) return null;

  return (
    <div className="inspection-checkup__grid">
      {visibleItems.map((item) => (
        <div key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

function displayDate(value) {
  const formatted = formatDate(value);
  return formatted === "-" ? "" : formatted;
}

function hasCoordinates(pole) {
  return parseCoordinate(pole?.lat, "lat") !== null && parseCoordinate(pole?.lng, "lng") !== null;
}

function SummaryPanel({ summary, loading, error, year, viewportLoading }) {
  if (loading && !summary) {
    return <div className="inspection-empty">{year} йил статистикаси юкланмоқда...</div>;
  }

  if (error && !summary) {
    return <div className="inspection-error">{error}</div>;
  }

  const items = [
    { label: "Линиялар", value: formatNumber(summary?.totalLines) },
    { label: "Тўлиқ", value: formatNumber(summary?.completedLines) },
    { label: "Қисман", value: formatNumber(summary?.partialLines) },
    { label: "Бошланмаган", value: formatNumber(summary?.notStartedLines) },
    { label: "Жами таянчлар", value: formatNumber(summary?.totalPoles) },
    { label: "Координатали", value: formatNumber(summary?.mappablePoles) },
    { label: "Координатасиз", value: formatNumber(summary?.withoutCoordinates) },
    { label: "Кўрикдан ўтган", value: formatNumber(summary?.inspected) },
    { label: "Кўрикдан ўтмаган", value: formatNumber(summary?.uninspected) },
    { label: "Камчиликлар билан", value: formatNumber(summary?.problem) },
    { label: "Авария ҳолатида", value: formatNumber(summary?.emergency) },
  ];

  return (
    <div className="inspection-panel">
      <div className="inspection-panel__headline">
        <span>Бажарилиш: {year}</span>
        <strong>{formatPercent(summary?.completionPct)}</strong>
      </div>
      <Progress value={summary?.completionPct} />
      <StatGrid items={items} />
      {loading && <div className="inspection-hint">Статистика янгиланмоқда...</div>}
      {viewportLoading && <div className="inspection-hint">Харита маълумотлари янгиланмоқда...</div>}
      {error && <div className="inspection-error">{error}</div>}
    </div>
  );
}

function PoleShortList({ title, list, status, onPoleSelect }) {
  const items = list?.items || [];
  if (!list?.total) return null;

  return (
    <div className="inspection-list">
      <div className="inspection-list__title">
        <span>{title}</span>
        <span>{formatNumber(list.total)}</span>
      </div>
      {items.map((pole) => {
        const canOpen = hasCoordinates(pole);
        return (
          <button
            key={pole.id}
            type="button"
            className="inspection-list__row"
            disabled={!canOpen}
            title={canOpen ? "" : "Координата йўқ"}
            onClick={() => canOpen && onPoleSelect?.(pole)}
          >
            <span className={`inspection-status-chip ${statusClass(status || pole.inspectionStatus)}`}>
              {statusLabel(status || pole.inspectionStatus)}
            </span>
            <span className="inspection-list__name">Таянч {pole.name || pole.id}</span>
            {pole.performed_at && <span className="inspection-list__date">{formatDate(pole.performed_at)}</span>}
            {!canOpen && <span className="inspection-list__no-coord">Координата йўқ</span>}
          </button>
        );
      })}
      {list.hasMore && <div className="inspection-list__more">Яна {formatNumber(list.total - items.length)}</div>}
    </div>
  );
}

function LinePanel({ data, year, onPoleSelect }) {
  const stats = data?.stats || {};
  const line = data?.line || {};
  const items = [
    { label: "Жами таянчлар", value: formatNumber(stats.totalPoles) },
    { label: "Координатали", value: formatNumber(stats.mappablePoles) },
    { label: "Координатасиз", value: formatNumber(stats.withoutCoordinates) },
    { label: "Кўрикдан ўтган", value: formatNumber(stats.inspected) },
    { label: "Кўрикдан ўтмаган", value: formatNumber(stats.uninspected) },
    { label: "Камчиликлар билан", value: formatNumber(stats.problem) },
    { label: "Авария ҳолатида", value: formatNumber(stats.emergency) },
  ];

  return (
    <div className="inspection-panel">
      <InlineMeta items={[line.city, line.voltage ? `${line.voltage} кВ` : ""]} />
      <div className="inspection-panel__headline">
        <span>{line.name || "Линия"}</span>
        <strong>{formatPercent(stats.completionPct)}</strong>
      </div>
      <Progress value={stats.completionPct} />
      <span className={`inspection-status-chip ${statusClass(stats.status)}`}>
        {statusLabel(stats.status)}
      </span>
      <StatGrid items={items} />
      <PoleShortList title={`${year} йилда кўрикдан ўтмаган`} list={data?.lists?.uninspected} status="uninspected" onPoleSelect={onPoleSelect} />
      <PoleShortList title="Камчиликлар билан" list={data?.lists?.problem} status="problem" onPoleSelect={onPoleSelect} />
      <PoleShortList title="Авария ҳолатида" list={data?.lists?.emergency} status="emergency" onPoleSelect={onPoleSelect} />
    </div>
  );
}

function CheckupBlock({ title, checkup, muted, emptyText }) {
  const [gallery, setGallery] = useState({ open: false, items: [], index: 0 });
  const files = Array.isArray(checkup?.files)
    ? checkup.files.filter((file) => hasDisplayValue(file?.directus_files_id))
    : [];
  const reasonEntries = negativeReasonEntries(checkup?.negative_reasons);

  const galleryItems = useMemo(
    () =>
      files.map((file, index) => ({
        id: file.directus_files_id,
        title: `Расм ${index + 1}`,
        thumb: `${DIRECTUS_URL}/assets/${file.directus_files_id}?key=thumb-480`,
        full: `${DIRECTUS_URL}/assets/${file.directus_files_id}`,
      })),
    [files]
  );

  if (!checkup) {
    return <div className="inspection-empty">{emptyText || `${title}: маълумот йўқ`}</div>;
  }

  const fields = [
    { label: "Сана", value: displayDate(checkup.performed_at || checkup.date_created) },
    { label: "Инспектор 1", value: fullName(checkup.inspector_user) },
    { label: "Инспектор 2", value: fullName(checkup.inspector_2_user) },
    { label: "GPS ҳолати", value: formatVerificationStatus(checkup.verification_status) },
    { label: "Аниқлик", value: formatMeters(checkup.accuracy_m) },
    { label: "Масофа", value: formatMeters(checkup.distance_to_pole_m) },
  ];

  return (
    <div className={`inspection-checkup ${muted ? "is-muted" : ""}`}>
      <div className="inspection-checkup__title">{title}</div>
      <FieldGrid items={fields} />

      {checkup.is_emergency && (
        <span className="inspection-status-chip is-emergency">Авария ҳолатида</span>
      )}

      {hasDisplayValue(checkup.notes) && (
        <div className="inspection-note">
          <span>Изоҳ</span>
          <p>{checkup.notes}</p>
        </div>
      )}

      {reasonEntries.length > 0 && (
        <div className="inspection-note inspection-note--reasons">
          <span>Камчилик сабаблари</span>
          <div className="inspection-reason-list">
            {reasonEntries.map((item) => (
              <div key={item.key} className="inspection-reason">
                <strong>{item.label}</strong>
                <p>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="inspection-photos">
          {galleryItems.map((item, index) => (
            <button
              key={item.id || index}
              type="button"
              className="inspection-photo"
              onClick={() => setGallery({ open: true, items: galleryItems, index })}
            >
              <img src={item.thumb} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <SchemeModal
        open={gallery.open}
        items={gallery.items}
        index={gallery.index}
        onClose={() => setGallery((prev) => ({ ...prev, open: false }))}
        onChangeIndex={(index) => setGallery((prev) => ({ ...prev, index }))}
      />
    </div>
  );
}

function PolePanel({ data, year, onShareLocation }) {
  const pole = data?.pole || {};
  const status = data?.status || {};

  return (
    <div className="inspection-panel">
      <div className="inspection-panel__headline">
        <span>Таянч {pole.name || pole.id}</span>
        <span className={`inspection-status-chip ${statusClass(status.inspectionStatus)}`}>
          {statusLabel(status.inspectionStatus)}
        </span>
      </div>
      <InlineMeta items={[pole.network, pole.city, pole.voltage ? `${pole.voltage} кВ` : ""]} />

      <StatGrid
        items={[
          { label: "Йил", value: year },
          { label: "Кўрикдан ўтган", value: status.inspectedCurrentYear ? "Ҳа" : "Йўқ" },
          { label: "GPS ҳолати", value: formatVerificationStatus(status.verification_status) },
          { label: "Координаталар", value: pole.hasCoordinates ? "Бор" : "Йўқ" },
        ]}
      />

      <CheckupBlock
        title={`${year} йилги кўрик`}
        emptyText={`${year} йилги кўрик маълумоти йўқ`}
        checkup={data?.currentYearCheckup}
      />
      {!data?.currentYearCheckup && (
        <CheckupBlock
          title="Охирги маълум кўрик"
          emptyText="Охирги маълум кўрик маълумоти йўқ"
          checkup={data?.lastKnownCheckup}
          muted
        />
      )}

      {pole.hasCoordinates && (
        <button
          type="button"
          className="map-info-btn"
          onClick={() => onShareLocation?.(pole.longitude, pole.latitude)}
        >
          Google Mapsда очиш
        </button>
      )}
    </div>
  );
}

export default function InspectionInfoPanel({
  year,
  summary,
  summaryLoading,
  summaryError,
  selected,
  viewportLoading,
  onClose,
  onClearSelection,
  onShareLocation,
  onPoleSelect,
}) {
  const title = selected?.type === "line"
    ? "Линия кўриги"
    : selected?.type === "pole"
      ? "Таянч кўриги"
      : "Кўрик назорати";

  return (
    <div className="map-info-panel map-info-panel--inspection">
      <div className="map-info-panel__header">
        <div className="map-info-panel__header-left">
          <div className="map-info-panel__title">{title}</div>
          <div className="map-info-panel__subtitle">{year} йил</div>
        </div>
        <button
          className="map-info-panel__close"
          onClick={() => {
            onClearSelection?.();
            onClose?.();
          }}
          aria-label="Ёпиш"
          title="Ёпиш"
        >
          x
        </button>
      </div>

      <div className="map-info-panel__body">
        {selected?.loading && <div className="inspection-empty">Маълумот юкланмоқда...</div>}
        {selected?.error && <div className="inspection-error">{selected.error}</div>}
        {!selected && (
          <SummaryPanel
            summary={summary}
            loading={summaryLoading}
            error={summaryError}
            year={year}
            viewportLoading={viewportLoading}
          />
        )}
        {!selected?.loading && !selected?.error && selected?.type === "line" && (
          <LinePanel data={selected.data} year={year} onPoleSelect={onPoleSelect} />
        )}
        {!selected?.loading && !selected?.error && selected?.type === "pole" && (
          <PolePanel data={selected.data} year={year} onShareLocation={onShareLocation} />
        )}
      </div>
    </div>
  );
}
